import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Trophy,
  RefreshCw,
  Gift,
  Coins,
  History,
  Star,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { UserModelData } from "../../../types";
import { actions } from "../../redux";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import LoadingIndicator from "../../components/LoadingIndicator";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

interface SpinReward {
  label: string;
  value: number;
  type: "points" | "picoin" | "multiplier" | "empty";
  color: string;
  weight: number; // Higher weight = higher chance
}

const REWARDS: SpinReward[] = [
  { label: "10 Points", value: 10, type: "points", color: "#6366f1", weight: 25 },
  { label: "0.001 Pi", value: 0.001, type: "picoin", color: "#f59e0b", weight: 2 }, // Rare: ~2%
  { label: "50 Points", value: 50, type: "points", color: "#8b5cf6", weight: 20 },
  { label: "Retry", value: 0, type: "empty", color: "#94a3b8", weight: 15 },
  { label: "100 Points", value: 100, type: "points", color: "#ec4899", weight: 10 },
  { label: "0.01 Pi", value: 0.01, type: "picoin", color: "#10b981", weight: 2 }, // Very Rare: ~2%
  { label: "20 Points", value: 20, type: "points", color: "#3b82f6", weight: 22 },
  { label: "Lucky Tag", value: 1, type: "multiplier", color: "#f43f5e", weight: 4 },
];

const LuckySpinPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getTranslationText = useTranslationHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState<SpinReward | null>(null);
  const [lastSpinDate, setLastSpinDate] = useState<Date | null>(null);
  const [canSpin, setCanSpin] = useState(true);
  const [timeToNextSpin, setTimeToNextSpin] = useState("");

  const controls = useAnimation();

  useEffect(() => {
    // Check last spin date from localStorage for now, will implement backend later
    const storedLastSpin = localStorage.getItem(`lastSpin_${user?.id}`);
    if (storedLastSpin) {
      const lastDate = new Date(parseInt(storedLastSpin));
      setLastSpinDate(lastDate);
      
      const checkCooldown = () => {
        const now = new Date();
        const diff = now.getTime() - lastDate.getTime();
        const cooldown = 24 * 60 * 60 * 1000; // 24 hours
        
        if (diff < cooldown) {
          setCanSpin(false);
          const remaining = cooldown - diff;
          const hours = Math.floor(remaining / (1000 * 60 * 60));
          const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
          setTimeToNextSpin(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setCanSpin(true);
          setTimeToNextSpin("");
        }
      };

      checkCooldown();
      const interval = setInterval(checkCooldown, 1000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const handleSpin = async () => {
    const hasExtraSpins = (user?.extraSpins || 0) > 0;
    if (isSpinning || (!canSpin && !hasExtraSpins)) return;

    setIsSpinning(true);
    setResult(null);

    // If using an extra spin (cooldown still active)
    const usingExtra = !canSpin && hasExtraSpins;


    // Weighted random selection
    const totalWeight = REWARDS.reduce((sum, r) => sum + r.weight, 0);
    const roll = Math.random() * totalWeight;
    let cumulative = 0;
    let randomIndex = 0;
    for (let i = 0; i < REWARDS.length; i++) {
      cumulative += REWARDS[i].weight;
      if (roll < cumulative) {
        randomIndex = i;
        break;
      }
    }
    const reward = REWARDS[randomIndex];
    
    // Calculate rotation: 5 full spins + target segment
    // Segment width = 360 / 8 = 45 degrees
    const extraDegrees = 360 - (randomIndex * 45) - 22.5; // Center of segment
    const newRotation = rotation + (360 * 5) + extraDegrees;
    
    setRotation(newRotation);

    // Animation will take 4 seconds
    setTimeout(async () => {
      setIsSpinning(false);
      setResult(reward);
      
      // Award prize
      try {
        if (usingExtra) {
          await arthereumProvider.useExtraSpin(user!.id);
        } else {
          // Update cooldown only if not using an extra spin
          const now = new Date();
          localStorage.setItem(`lastSpin_${user?.id}`, now.getTime().toString());
          setLastSpinDate(now);
          setCanSpin(false);
        }

        await arthereumProvider.awardSpinPrize(user!.id, reward as any);
        
        // Update Redux locally instead of re-fetching from Firestore
        const balanceUpdates: any = {};
        if (reward.type === "picoin") balanceUpdates.picoinBalance = (user!.picoinBalance || 0) + reward.value;
        if (reward.type === "points") balanceUpdates.arthereumBalance = (user!.arthereumBalance || 0) + reward.value;
        if (usingExtra) balanceUpdates.extraSpins = Math.max(0, (user!.extraSpins || 0) - 1);
        dispatch(actions.setActualUser({
          ...userData,
          user: { ...user, ...balanceUpdates },
        }));

        // Fire rewarded ad in the background after spin ends
        // It's still forced but now pays as a rewarded ad.
        piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
      } catch (error) {
        console.error("Error awarding spin prize:", error);
      }
    }, 4000);
  };

  const handleAdForSpin = async () => {
    if (isSpinning) return;
    
    try {
      const adSuccess = await piNetworkAdsManager.handleRewardedAdFlowFront(user!.id,()=>{
        setCanSpin(true);
        setResult(null);
        alert("Ad watched! You've earned an extra spin! 🍀 Wow!");
      });
    } catch (error) {
      console.error("Ad failed for spin:", error);
    }
  };
  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <CommonPageTitle title="Lucky Spin" padding={null} />

      <div className="max-w-md mx-auto px-4 py-8 space-y-8">
        {/* Header Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center">
            <Trophy className="text-amber-500 mb-1" size={20} />
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Streak</span>
            <span className="text-xl font-bold text-slate-900">{user?.currentStreak || 0} Days</span>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center">
            <Coins className="text-indigo-500 mb-1" size={20} />
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Balance</span>
            <span className="text-xl font-bold text-slate-900">{user?.arthereumBalance?.toFixed(2) || "0.00"}</span>
          </div>
        </div>

        {/* Wheel Container */}
        <div className="relative flex justify-center py-10">
          {/* Pointer */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-8 h-8 bg-slate-900 rotate-45 rounded-sm shadow-lg flex items-center justify-center -translate-y-2">
               <div className="w-4 h-4 bg-white rounded-full" />
            </div>
          </div>

          {/* The Wheel */}
          <motion.div
            className="w-72 h-72 rounded-full border-8 border-slate-900 shadow-2xl relative overflow-hidden flex items-center justify-center bg-slate-900"
            animate={{ rotate: rotation }}
            transition={{ duration: 4, ease: "easeOut" }}
          >
            {REWARDS.map((reward, index) => (
              <div
                key={index}
                className="absolute w-full h-full"
                style={{
                  transform: `rotate(${index * 45}deg)`,
                }}
              >
                <div
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-1/2 origin-bottom"
                  style={{
                    backgroundColor: reward.color,
                    clipPath: "polygon(50% 100%, 0 0, 100% 0)",
                  }}
                >
                   <div className="absolute top-8 left-1/2 -translate-x-1/2 -rotate-90 origin-center whitespace-nowrap text-white text-[10px] font-bold">
                    {reward.label}
                   </div>
                </div>
              </div>
            ))}
            
            {/* Center Circle */}
            <div className="absolute w-12 h-12 bg-slate-900 rounded-full border-4 border-white shadow-inner flex items-center justify-center">
               <Sparkles className="text-amber-400" size={20} />
            </div>
          </motion.div>
        </div>

        {/* Spin Button */}
        <div className="text-center space-y-4">
          <button
            onClick={handleSpin}
            disabled={isSpinning || !canSpin}
            className={`
              w-full py-4 rounded-2xl font-bold text-lg shadow-lg transition-all transform active:scale-95
              ${isSpinning || !canSpin 
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-indigo-200'
              }
            `}
          >
            {isSpinning ? (
              <div className="flex items-center justify-center gap-2">
                <RefreshCw className="animate-spin" size={20} />
                <span>Spinning...</span>
              </div>
            ) : canSpin ? (
              "Spin Now!"
            ) : (user?.extraSpins || 0) > 0 ? (
              <div className="flex flex-col">
                <span>Use Extra Spin! ✨</span>
                <span className="text-[10px] opacity-75">{user?.extraSpins} remaining</span>
              </div>
            ) : (
              "Next Spin in " + timeToNextSpin
            )}
          </button>


         
          <p className="text-sm text-slate-500 italic">
            Win Pi, Points and special bonuses every 24 hours!
          </p>
        </div>

        {/* Result Animation */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-indigo-600 p-6 rounded-3xl text-white text-center shadow-xl shadow-indigo-200 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Gift size={80} />
              </div>
              <h3 className="text-indigo-100 font-medium uppercase tracking-widest text-xs mb-2">Congratulations!</h3>
              <p className="text-2xl font-black mb-1">
                {result.type === "empty" ? "Better luck next time!" : `You won ${result.label}!`}
              </p>
              <p className="text-indigo-200 text-sm">
                {result.type === "empty" ? "The wheel wasn't in your favor today." : "Reward has been added to your account."}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Watch Ad to Spin Again — always visible after a spin result */}
        {result !== null && !isSpinning && (
          <button
            onClick={handleAdForSpin}
            className="w-full py-4 rounded-2xl font-bold bg-amber-50 text-amber-700 border-2 border-amber-200 border-dashed hover:bg-amber-100 transition-all flex items-center justify-center gap-2 group"
          >
            <RefreshCw className="group-hover:rotate-180 transition-transform duration-500" size={18} />
            <span>🎬 Watch Ad to Spin Again!</span>
          </button>
        )}

        {/* Info Cards */}
        <div className="space-y-3">
          <div className="bg-white p-4 rounded-2xl border border-slate-100 flex gap-4">
             <div className="bg-amber-50 p-2 rounded-xl h-fit">
                <Star className="text-amber-500" size={20} />
             </div>
             <div>
                <h4 className="font-bold text-slate-900 text-sm">Daily Bonus</h4>
                <p className="text-xs text-slate-500">Come back daily to keep your streak and claim free rewards.</p>
             </div>
          </div>
          <div className="bg-white p-4 rounded-2xl border border-slate-100 flex gap-4">
             <div className="bg-indigo-50 p-2 rounded-xl h-fit">
                <Info className="text-indigo-500" size={20} />
             </div>
             <div>
                <h4 className="font-bold text-slate-900 text-sm">Contest Points</h4>
                <p className="text-xs text-slate-500">Winning points also helps you rank higher in the daily Pi contest!</p>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LuckySpinPage;
